import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpInterceptorService } from '@ispm/core/services/http-interceptor.service';
import {IspmHttpClient} from "@ispm/core/services/ispm-http-client";
import {map, mergeMap} from "rxjs/operators";
interface Part {
  header: string;
  info: string;
  part: number[];
}

@Injectable()
export class MultiFileParsingService {
  constructor(
    private _http: HttpInterceptorService,
    private _ispmHttpClient: IspmHttpClient
  ) {

  }
  private fileCache: Map<string, Observable<any>> = new Map();

  /**
   * Finds a given token in a message at a given offset.
   * @param {Uint8Array} message message content
   * @param {Uint8Array} token substring that should be found
   * @param {String} offset message body offset from where search should start
   * @param maxSearchLength
   * @returns {Boolean} whether message has a part at given offset or not
   */
  private findToken(message: Uint8Array, token: Uint8Array, offset: number = 0, maxSearchLength?: number) {
    let searchLength = message.length;

    if (maxSearchLength) {
      searchLength = Math.min(offset + maxSearchLength, message.length);
    }
    let index = offset;
    for (let i = offset; i < searchLength; i++) {
      index = i;
      if (message[i] === token[0]) {
        if (this.containsToken(message, token, i)) {
          break;
        }
      }
    }

    return index < searchLength - 1 ? index : -1;
  }

  /**
   * Checks whether a given token is contained by a message at a given offset.
   * @param {Uint8Array} message message content
   * @param {Uint8Array} token substring that should be present
   * @param {Number} offset offset in message content from where search should start
   * @returns {Boolean} whether message contains token at offset
   */
  private containsToken(message: Uint8Array, token: Uint8Array, offset: number = 0) {
    if (offset + token.length > message.length) {
      return false;
    }
    let index = offset;
    for (let i = 0; i < token.length; i++) {
      if (token[i] !== message[index]) {
        return false;
      }
      index += 1;
    }
    return true;
  }

  /**
   * multipart to decode the MIME response
   * @param response
   * @param boundaryString
   * return arrayBufferOutput
   */
  private multipartDecode(response: ArrayBuffer, boundaryString: string): ArrayBuffer {
    if (!boundaryString) {
      throw new Error('Header of response message does not specify boundary');
    }
    const message = new Uint8Array(response);
    /* Max Length to minimize the recursive loop if token not available*/
    const maxSearchLength = 1000;
    const separator = new Uint8Array([13, 10, 13, 10]); // separator : "\r\n\r\n"
    let headerIndex = this.findToken(message, separator, 0, maxSearchLength);
    if (headerIndex === -1) {
      const separator = new Uint8Array([13, 10]); // separator : "\r\n"
      const alternativeHeaderIndex = this.findToken(message, separator, 0, maxSearchLength);
      if (alternativeHeaderIndex === -1) {
        throw new Error('Response message has no multipart mime header');
      }
      headerIndex = alternativeHeaderIndex;
    }
    const boundary = this.stringToUint8Array(boundaryString);
    const boundaryLength = boundary.length;
    let offset = boundaryLength;        // Looping to find complete boundary information
    let boundaryIndex = undefined;
    let arrayBufferOutput;
    while (boundaryIndex !== -1) {
      // Search for the next boundary in the message, starting from the current offset position
      boundaryIndex = this.findToken(message, boundary, offset); // If no further boundaries are found, stop here.
      if (boundaryIndex === -1) {
        break;
      }
      let headerTokenIndex = this.findToken(message, separator, offset, maxSearchLength);
      if (headerTokenIndex === -1) {
        const separator = new Uint8Array([13, 10]); // separator : "\r\n"
        const alternativeHeaderIndex = this.findToken(message, separator, offset, maxSearchLength);
        if (alternativeHeaderIndex === -1) {
          throw new Error('Response message part has no mime header');
        }
        headerTokenIndex = alternativeHeaderIndex;
      }

      offset = headerTokenIndex + separator.length; // Extract data from response message, excluding "\r\n"

      const spacingLength = 2;
      arrayBufferOutput = response.slice(offset, boundaryIndex - spacingLength); // Add the data to the array of results

      offset = boundaryIndex + boundaryLength;
    }
    return arrayBufferOutput;
  }

  /**
   * Conversion from String to respective Uint8Array
   * @param str
   * return Uint8Array
   */
  private stringToUint8Array(str: string) {
    const arr = new Uint8Array(str.length);
    for (let i = 0, j = str.length; i < j; i++) {
      arr[i] = str.charCodeAt(i);
    }
    return arr;
  }

  /**
   *  Get the Boundary value from the content-type header sent by the http client
   *  this value may be similar to:
   *  'multipart/related; type="image/dicom-jpeg"; boundary=2bca0ff5-5668-4117-97ad-01708b1ecb6d'
   * @param header
   */
  getBoundary(header: string): string {
    const items = header.split(';');
    if (items) {
      for (let i = 0; i < items.length; i++) {
        const item = String(items[i]).trim();
        if (item.indexOf('boundary') >= 0) {
          const k = item.split('=');
          return String(k[1]).trim();
        }
      }
    }
    return '';
  }

  getBoundaryFromBody(response: any) {
    const message = new Uint8Array(response);
    /* Max Length to minimize the recursive loop if token not available*/
    const maxSearchLength = 1000;
    const separator = new Uint8Array([13, 10]); // separator : "\r\n"
    const headerIndex = this.findToken(message, separator, 0, maxSearchLength);
    let boundary = '';
    for (let i = 2; i < headerIndex; i++) {
      boundary += String.fromCharCode(message[i]);
    }
    return boundary;
  }


  /**
   * Get File From WADO URL
   */
  getFileFromWADOUrl(file, studyId, patientId): any {
    const instaceId = file.instanceId;
    let preSignPayload = {
      "studyID": studyId,
      "instanceId": instaceId,
      "patientId": patientId,
      "wadoUrl": file.reference,
      "fileType": file.fileType
    };

    if (this.fileCache.has(instaceId)) {
      return this.fileCache.get(instaceId);
    } else {
      return this.preSignedUrl(preSignPayload).pipe(mergeMap((response: any) => {
        const type = file.contentType === 'mp4' ? 'video/mp4' : file.contentType === 'pdf' ? 'application/octet-stream' : 'image/jpeg';
        return this._http.get(
          response.url,
          {},
          false,
          {'Accept': `multipart/related; type=${type}`},
          {responseType: 'arraybuffer'}
        )
          .map((response) => {
            // getting all of the Response Headers
            const contentTypeHeader = response.headers.get('Content-Type');
            const boundary = this.getBoundaryFromBody(response.body);
            let decodedData = this.multipartDecode(response.body, boundary);
            this.fileCache.set(instaceId, Observable.of(decodedData));
            return decodedData
          });
        ``
      }));
    }
  }

  getInstanceId(url: string) {
    let uri = new URL(url);
    let pathArray = uri.pathname.split("/");
    return pathArray[pathArray.length - 1];
  }

  preSignedUrl(body): any {
    return this._ispmHttpClient.post('preSignUrl', body)
  }
}
